/**
 * Teléfonos en el front. Copia MÍNIMA y deliberada de `api/src/lib/phone.ts`.
 *
 * POR QUÉ NO SE COPIA ENTERA: la función canónica del servidor trae la tabla de
 * ~230 ladas de país y las reglas por región. El navegador no necesita nada de
 * eso: aquí sólo se MUESTRA un número, no se decide a quién enviarle. Copiar las
 * 200 líneas sería peso muerto en la PWA y, peor, dos copias que se van
 * separando sin que nadie lo note.
 *
 * POR QUÉ SÍ SE COPIA EL QUITAR EL DÍGITO TRONCAL: es la única transformación
 * VISIBLE. Si el servidor manda a +52 55… y la pantalla dice +521 55…, quien
 * atiende cree que le está escribiendo a otro número — y ese desfase es
 * exactamente lo que hizo tan difícil de rastrear el error 131030.
 *
 * REGLA: si esto cambia, cambia también `api/src/lib/phone.ts`. Lo que decide
 * a dónde se envía es SIEMPRE el servidor; esto es presentación.
 */

/** Quita el dígito troncal que Meta inyecta en los wa_id de México (1) y
 *  Argentina (9). Ninguna lada de país es "521" ni "549", así que es seguro. */
export function stripMetaTrunkDigit(raw: string): string {
  const d = (raw || "").replace(/\D/g, "");
  if (/^521\d{10}$/.test(d)) return "52" + d.slice(3);
  if (/^549\d{10,11}$/.test(d)) return "54" + d.slice(3);
  return d;
}

/** Clave de identidad: últimos 10 dígitos. Misma regla que el servidor. */
export function phoneKey(p?: string | null): string {
  const d = String(p ?? "").replace(/\D/g, "");
  return d.length >= 10 ? d.slice(-10) : "";
}

/**
 * Liga de WhatsApp lista para abrir. Usa la MISMA forma que el servidor manda,
 * no una regla propia.
 *
 * ANTES había dos versiones escritas a mano —una en `MiDia.tsx` y otra dentro
 * del JSX de `Ficha.tsx`— y las dos hacían sólo "si son 10 dígitos, ponle 52".
 * Ninguna quitaba el "1" troncal que Meta mete en los móviles mexicanos, así
 * que a una paciente guardada como `5215512345678` la asesora le abría
 * `wa.me/5215512345678`: un número distinto del que el sistema usa para
 * enviarle. Cuando eso falla, parece que "WhatsApp anda raro".
 */
export function waLink(num: string | null | undefined, texto?: string): string | null {
  if (!num) return null;
  let d = stripMetaTrunkDigit(num);
  if (!d) return null;
  // Sólo se completa la lada de un número NACIONAL pelón. Uno que ya la trae
  // —incluido el de una paciente extranjera— no se toca.
  if (d.length === 10) d = '52' + d;
  return `https://wa.me/${d}${texto ? `?text=${encodeURIComponent(texto)}` : ''}`;
}

/**
 * Un teléfono guardado en E.164 con "+" (el de las cuentas del personal, api/src/lib/cuentas.ts) como se lee:
 * "+525512345678" → "+52 55 1234 5678"; "+13055551234" → "+1 305 555 1234". Otro país: tal cual.
 */
export function telefonoLegible(t: string | null | undefined): string {
  const s = String(t ?? '').trim();
  const m52 = /^\+52(\d{2})(\d{4})(\d{4})$/.exec(s);
  if (m52) return `+52 ${m52[1]} ${m52[2]} ${m52[3]}`;
  const m1 = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(s);
  if (m1) return `+1 ${m1[1]} ${m1[2]} ${m1[3]}`;
  return s;
}
